import {
  searchContactsQuick,
  searchDealsQuick,
  searchCompaniesQuick,
  type QuickContact,
  type QuickDeal,
  type QuickCompany,
} from '@/features/hubspot/services/hubspotApi'
import { getCompanyProfile } from '@/features/company-profile/services/companyProfileApi'
import { listIcps } from '@/features/icps/services/icpsApi'
import type { CrmEntity, CrmEntityType } from '../types'

export type { CrmEntity, CrmEntityType }

/**
 * Live search across connected workspace CRM backend endpoints:
 * - Contacts: HubSpot CRM contacts API
 * - Deals: HubSpot CRM deals API
 * - Leads: HubSpot CRM companies/accounts API
 * - KB: Workspace Company Profile & Knowledge
 *
 * Returns only real data. No dummy or fallback data is generated.
 */
export async function searchCrmEntities(
  workspaceId?: string | null,
  query: string = '',
  category: 'all' | CrmEntityType = 'all'
): Promise<CrmEntity[]> {
  if (!workspaceId) {
    return []
  }

  const cleanQuery = query.trim().toLowerCase()
  const results: CrmEntity[] = []

  const fetchContacts = async (): Promise<CrmEntity[]> => {
    if (category !== 'all' && category !== 'contact') return []
    try {
      const contacts: QuickContact[] = await searchContactsQuick(workspaceId, cleanQuery)
      return contacts.map((c) => ({
        id: c.id,
        type: 'contact',
        title: c.name || 'Unnamed Contact',
        subtitle: [c.company, c.email].filter(Boolean).join(' · '),
        metadata: {
          id: c.id,
          name: c.name,
          email: c.email,
          company: c.company,
          source: 'hubspot',
        },
      }))
    } catch (err) {
      console.warn('[CRM Search] Contacts fetch error:', err)
      return []
    }
  }

  const fetchDeals = async (): Promise<CrmEntity[]> => {
    if (category !== 'all' && category !== 'deal') return []
    try {
      const deals: QuickDeal[] = await searchDealsQuick(workspaceId, cleanQuery)
      return deals.map((d) => ({
        id: d.id,
        type: 'deal',
        title: d.name || 'Unnamed Deal',
        subtitle: [d.amount, d.stage ? `Stage: ${d.stage}` : '', d.closeDate ? `Close: ${d.closeDate}` : ''].filter(Boolean).join(' · '),
        metadata: {
          id: d.id,
          name: d.name,
          amount: d.amount,
          stage: d.stage,
          closeDate: d.closeDate,
          source: 'hubspot',
        },
      }))
    } catch {
      return []
    }
  }

  const fetchLeads = async (): Promise<CrmEntity[]> => {
    if (category !== 'all' && category !== 'lead') return []
    try {
      const companies: QuickCompany[] = await searchCompaniesQuick(workspaceId, cleanQuery)
      return companies.map((c) => ({
        id: c.id,
        type: 'lead',
        title: c.name || 'Unnamed Account',
        subtitle: [c.industry, c.domain].filter(Boolean).join(' · '),
        metadata: {
          id: c.id,
          name: c.name,
          domain: c.domain,
          industry: c.industry,
          source: 'hubspot',
        },
      }))
    } catch {
      return []
    }
  }

  const fetchKb = async (): Promise<CrmEntity[]> => {
    if (category !== 'all' && category !== 'kb') return []
    try {
      const [profile, icps] = await Promise.all([getCompanyProfile(workspaceId), listIcps(workspaceId).catch(() => [])])
      const identity = profile?.identity
      if (!identity) return []

      const kbItems: CrmEntity[] = []

      // Products & services
      identity.productsServices?.forEach((prod, idx) => {
        const matches = !cleanQuery || prod.name.toLowerCase().includes(cleanQuery) || prod.description.toLowerCase().includes(cleanQuery)
        if (matches) {
          kbItems.push({
            id: `kb_prod_${idx}`,
            type: 'kb',
            title: prod.name,
            subtitle: prod.description,
            metadata: { section: 'Products & Services', name: prod.name, description: prod.description },
          })
        }
      })

      // Value propositions
      identity.valuePropositions?.forEach((vp, idx) => {
        const matches = !cleanQuery || vp.title.toLowerCase().includes(cleanQuery) || vp.description.toLowerCase().includes(cleanQuery)
        if (matches) {
          kbItems.push({
            id: `kb_vp_${idx}`,
            type: 'kb',
            title: vp.title,
            subtitle: vp.description,
            metadata: { section: 'Value Propositions', title: vp.title, description: vp.description },
          })
        }
      })

      // Ideal customer profiles — one entry per ICP
      icps.forEach((icp) => {
        const icpSummary = [
          icp.industries.length ? `Industries: ${icp.industries.join(', ')}` : '',
          icp.jobTitles.length ? `Titles: ${icp.jobTitles.join(', ')}` : '',
        ].filter(Boolean).join(' · ')

        const matches = !cleanQuery || icp.name.toLowerCase().includes(cleanQuery) || icpSummary.toLowerCase().includes(cleanQuery)
        if (matches && icpSummary) {
          kbItems.push({
            id: `kb_icp_${icp.id}`,
            type: 'kb',
            title: `ICP: ${icp.name}`,
            subtitle: icpSummary,
            metadata: { section: 'Ideal Customer Profile', name: icp.name, isPrimary: icp.isPrimary, industries: icp.industries, companySizes: icp.companySizes, jobTitles: icp.jobTitles },
          })
        }
      })

      // Differentiators
      if (identity.differentiators) {
        const matches = !cleanQuery || identity.differentiators.toLowerCase().includes(cleanQuery)
        if (matches) {
          kbItems.push({
            id: 'kb_diff',
            type: 'kb',
            title: 'Key Differentiators',
            subtitle: identity.differentiators,
            metadata: { section: 'Differentiators', content: identity.differentiators },
          })
        }
      }

      return kbItems
    } catch {
      return []
    }
  }

  const [contactsRes, dealsRes, leadsRes, kbRes] = await Promise.all([
    fetchContacts(),
    fetchDeals(),
    fetchLeads(),
    fetchKb(),
  ])

  results.push(...contactsRes, ...dealsRes, ...leadsRes, ...kbRes)
  return results
}
